/**
 * Export PDF report button + preview modal for Control Centre daily checks.
 * Matches Profit tab styling (emerald Export PDF report + modal preview).
 */

import { useCallback, useEffect, useState } from 'react';
import { FileDown, X } from 'lucide-react';
import {
  buildControlCentreDailyChecksPdf,
  type ControlCentreDailyChecksPdfOptions,
} from '../../utils/buildControlCentreDailyChecksPdf';

interface ControlCentreDailyChecksPdfExportProps {
  /** Driver source badge label (e.g. Aquarius) */
  driverSourceLabel?: string;
  /** Selected window (ISO) so the report matches the on-screen filter. */
  from?: string;
  to?: string;
  disabled?: boolean;
}

export default function ControlCentreDailyChecksPdfExport({
  driverSourceLabel,
  from,
  to,
  disabled = false,
}: ControlCentreDailyChecksPdfExportProps) {
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; filename: string; blob: Blob } | null>(
    null,
  );
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const closePreview = useCallback(() => {
    setPdfPreviewOpen(false);
    setPdfPreview((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);

  useEffect(() => {
    return () => {
      setPdfPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return null;
      });
    };
  }, []);

  const openPreview = useCallback(async () => {
    setPdfPreviewLoading(true);
    setPdfError(null);
    try {
      const opts: ControlCentreDailyChecksPdfOptions = { driverSourceLabel, from, to };
      const out = await buildControlCentreDailyChecksPdf(opts);
      if (!out) {
        setPdfError('Could not build the PDF report. Try again.');
        return;
      }
      setPdfPreview((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return { url: URL.createObjectURL(out.blob), filename: out.filename, blob: out.blob };
      });
      setPdfPreviewOpen(true);
    } catch {
      setPdfError('Could not build the PDF report. Try again.');
    } finally {
      setPdfPreviewLoading(false);
    }
  }, [driverSourceLabel, from, to]);

  const downloadPdf = useCallback(() => {
    if (!pdfPreview) return;
    const a = document.createElement('a');
    a.href = pdfPreview.url;
    a.download = pdfPreview.filename;
    a.click();
  }, [pdfPreview]);

  return (
    <>
      <div className="flex flex-col gap-1 shrink-0 ml-auto">
        <button
          type="button"
          onClick={() => {
            void openPreview();
          }}
          disabled={disabled || pdfPreviewLoading}
          title="Preview the Driver & Maintenance daily checks PDF for the selected period"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium border border-success/80 bg-success/40 text-success hover:bg-success/50 hover:border-success disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FileDown className="w-4 h-4 shrink-0" aria-hidden />
          {pdfPreviewLoading ? 'Preparing preview…' : 'Export PDF report'}
        </button>
        {pdfError ? <p className="text-xs text-destructive">{pdfError}</p> : null}
      </div>

      {pdfPreviewOpen && pdfPreview ? (
        <div
          className="fixed inset-0 z-sticky flex items-center justify-center p-2 sm:p-3 bg-black/70"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cc-daily-checks-pdf-preview-title"
        >
          <div className="bg-popover border border-border rounded-xl shadow-2xl w-full max-w-modal h-modal flex flex-col overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border shrink-0">
              <h2
                id="cc-daily-checks-pdf-preview-title"
                className="text-lg font-semibold text-white truncate pr-2"
              >
                PDF preview — {pdfPreview.filename}
              </h2>
              <button
                type="button"
                onClick={closePreview}
                className="p-2 rounded-lg text-muted-foreground hover:text-white hover:bg-accent"
                aria-label="Close preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground px-4 py-2 border-b border-border shrink-0">
              Driver and Maintenance daily checks for the selected period. Download when you are
              satisfied with this snapshot.
            </p>
            <iframe
              title="Daily checks PDF preview"
              src={`${pdfPreview.url}#toolbar=0`}
              className="w-full flex-1 min-h-0 bg-background border-0"
            />
            <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-border bg-background/90 shrink-0">
              <button
                type="button"
                onClick={closePreview}
                className="px-4 py-2 rounded-md text-sm font-medium text-foreground bg-secondary hover:bg-accent border border-border"
              >
                Close
              </button>
              <button
                type="button"
                onClick={downloadPdf}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-primary hover:bg-primary border border-primary"
              >
                <FileDown className="w-4 h-4" aria-hidden />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
